//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { makeStyles, Tooltip } from '@material-ui/core';
import InfoRoundedIcon from '@material-ui/icons/InfoOutlined';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
// import { getStateLabelByCountryName } from '../../common/locationHelper';
import useCountryStateCity from '../../common/hooks/useCountryStateCity';
// import { PLACEHOLDER_FIELD_COUNTRY, PLACEHOLDER_FIELD_STATE, PLACEHOLDER_FIELD_CITY, getStateLovPlaceholder, getCityLovPlaceholder } from '../../common/placeholderHelper';
import {
    CONFIRM_DIALOG_MESSAGE_CANCEL,
    CONFIRM_DIALOG_MESSAGE_DELETE_LOGO,
    CONFIRM_DIALOG_TITLE_CANCEL,
    CONFIRM_DIALOG_TITLE_DELETE_LOGO,
    TOOLTIP_EMP_MEMBER_COUNTRY,
    TOOLTIP_EMP_MEMBER_USE_COMPANY_LOCATION,
    USE_HEADQUARTER_LOCATION,
    UPLOADED_VIDEO_STATUS_PROCESSING,
    SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED,
} from '../../common/constants';
// import { createStripeCustomer } from '../../common/services/paymentServices';
// import { getUserInfoByEmployerId } from '../../common/employerHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { uploadProfilePicture, deleteProfilePicture, updateEmployerData, saveEmployerOwnerData, resetEmpSeResults } from '../../common/services/empMySettingsServices';
import {
    //  detectInappropriatePicture,
    extractBadContentFromResult,
    moderateUserTextInput,
} from '../../common/sightEngineHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
// import AfitdAutocompleteLite from '../../common/elemental/AfitdAutocompleteLite';
import AfitdDateFormatAutocomplete from '../../common/elemental/AfitdDateFormatAutocomplete';
import AfitdProfilePicCropper from '../../common/elemental/AfitdProfilePicCropper';
import AfitdCountryAutocomplete from '../../common/elemental/AfitdCountryAutocomplete';
import AfitdStateAutocomplete from '../../common/elemental/AfitdStateAutocomplete';
import AfitdCityAutocomplete from '../../common/elemental/AfitdCityAutocomplete';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import AfitdLinearProgress from '../../common/elemental/AfitdLinearProgress';
import AfitdAvatarWithRing from '../../common/elemental/AfitdAvatarWithRing';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdLink from '../../common/elemental/AfitdLink';
import AfitdRouterPrompt from '../../common/elemental/AfitdRouterPrompt';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import LoadingGif from '../../assets/loading.gif';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _set from 'lodash/set';
import _includes from 'lodash/includes';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const errorStyles = { color: 'red' };

    return makeStyles(theme => ({
        avatarPlaceholder: { width: theme.spacing(8), height: theme.spacing(8), borderRadius: '100%' },
        avatarPlaceholderBox: {
            width: 104,
            height: 104,
            background: '#FFFFFF',
            border: '1px dashed #D5D7DD',
            borderRadius: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxSizing: 'border-box',
        },
        btnBox: { padding: 10, paddingTop: 30 },
        instructionText: { marginTop: 5, color: 'rgba(17, 30, 20, 0.4)' },
        inputBoxContainer: { margin: '10px 0', [theme.breakpoints.up('md')]: { display: 'flex' } },
        inputBox: { flex: 1, padding: '0 5px' },
        label: { padding: '5px 0', fontWeight: 'bold' },
        dropDown: { fontSize: '16px', color: '#1D8A8B', border: '1px solid #D5D7DD', borderRadius: 4 },
        focusDropDown: { fontSize: '16px', color: '#1D8A8B', border: '2px solid #1D8A8B', borderRadius: 4 },
        buttonBox: {
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '10px 0',
            // [theme.breakpoints.down('sm')]: { justifyContent: 'space-between' },
            [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
        },
        heading: { fontWeight: 'bold' },

        rightMarginToLeftButton: { marginRight: 16, [theme.breakpoints.down('sm')]: { marginRight: 0 } },
        generalAvatarContainer: { margin: '30px 0', [theme.breakpoints.up('md')]: { display: 'flex' }, [theme.breakpoints.down('sm')]: { margin: '30px 0px 10px 0px' } },
        hidden: { display: 'none' },
        cropContainer: { height: 250, width: '100%', textAlign: 'center' },
        imgBeforeCropContainer: { display: 'flex', justifyContent: 'center' },
        uploadLinearProgressContainer: { marginTop: 30, padding: '0 20px' },
        generalMainContainer: { [theme.breakpoints.down('md')]: { paddingBottom: '50px' }, [theme.breakpoints.down('sm')]: { paddingBottom: '35px' } },
        generalSaveButton: { [theme.breakpoints.down('sm')]: { width: '100%', margin: '16px 0px' } },
        generalContinueButton: { [theme.breakpoints.down('sm')]: { width: '100%', margin: '16px 0px' } },

        useHeadquarterLocationLinkContainer: { display: 'flex', margin: '20px 0 0 5px' },
        useHeadquarterLocationLinkSpan: { marginRight: 5 },
        useHeadquarterLocationLink: { textDecoration: 'underline', color: 'blue' },
        infoIcon: {
            color: 'black',
            background: 'white',
            fontSize: '15px',
            cursor: 'pointer',
        },
        toolTipTitle: {
            backgroundColor: 'white',
            boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
            borderRadius: '11px',
            fontSize: '14px',
            padding: '8px',
            color: 'black',
        },

        companyNameErrorText: errorStyles,
        stateErrorText: errorStyles,
        countryErrorText: errorStyles,
        cityErrorText: errorStyles,
        // languageErrorText: errorStyles,
        dateFormatErrorText: errorStyles,
        timeFormatErrorText: errorStyles,
        memberCountryErrorText: errorStyles,
        memberStateErrorText: errorStyles,
        memberCityErrorText: errorStyles,
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        isDirty,
        // setIsDirty,
        pendingAccountSetup,
        data,
        country,
        // setCountry,
        state,
        // setState,
        // city,
        // setCity,
        // cityLocation,
        // setCityLocation,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
        classes,
        handleChangeFields,
        // handleChangeCountry,
        // handleChangeState,
        // handleChangeCity,
        handleSaveButton,
        handleClickContinueButton,
        handleClickConfirmCancelModalYesButton,
        handleUploadPic,
        handleDeletePic,
        // countryLov,
        // stateLov,
        // cityLov,
        // currentEmployer,
        showModal,
        setShowModal,
        showCropModal,
        setShowCropModal,
        handleChangePicUploadInput,
        setBlob,
        tempImage,
        errors,
        progress,
        props,
        confirmModal,
        setConfirmModalOpen,
        // handleChangeLanguage,
        handleChangeDateFormat,
        openBadWordsDialog,
        // setOpenBadWordsDialog,
        // handleChangeTimeFormat,
        badWords,
        // setBadWords,
        clearOutBadWords,
        saveLoading,
        openBadImageDialog,
        setOpenBadImageDialog,
        badImageDialogMessage,
        handleClickUseHeadquarterLocationLink,
        memberCountry,
        memberState,
        // memberCity,
        // memberCityLocation,
        memberCountryProps,
        memberStateLabelProps,
        memberStateProps,
        memberCityProps,
        employerId,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        isPictureProcessing,
    } = params;
    const { rightMarginToLeftButton: rightMarginToLeftButtonCls, hidden: hiddenCls } = classes;
    const { companyName = '', profilePicUrl = '', dateFormat = '' /* language = ''*/ } = data;
    const { companyNameError, countryError, stateError, cityError, memberCountryError, memberStateError, memberCityError, /* languageError,*/ timeFormatError, dateFormatError } =
        errors;
    const { isAdmin } = props;

    const heading = pendingAccountSetup ? 'Step 1: General Information' : 'General';
    // const LanguagesList = ['English', 'Spanish', 'Mandarin Chinese'];

    // const stateDisabled = !country;
    // const cityDisabled = !state || !country;
    // const stateLabelValue = getStateLabelByCountryName(country);
    // const statePlaceholder = stateDisabled ? '' : getStateLovPlaceholder(stateLov, PLACEHOLDER_FIELD_STATE);
    // const cityPlaceholder = cityDisabled ? '' : getCityLovPlaceholder(cityLov, PLACEHOLDER_FIELD_CITY);
    const badWordsList = badWords?.profanity?.join(', ');

    // const isDirty =
    //     companyName !== currentEmployer?.companyName ||
    //     profilePicUrl !== currentEmployer?.profilePicUrl ||
    //     // language !== currentEmployer?.language ||
    //     city !== currentEmployer?.city ||
    //     state !== currentEmployer?.state ||
    //     country !== currentEmployer?.country ||
    //     dateFormat !== currentEmployer?.dateFormat;

    const isProfilePicture = profilePicUrl || false;

    const defns = {
        profilePic: {
            photoSize: 96,
            borderSize: 4,
            placeholderFontSize: 30,
            src: profilePicUrl,
        },
        loadingPictureAvatar: {
            photoSize: 96,
            borderSize: 4,
            placeholderFontSize: 30,
            src: LoadingGif,
        },
        generalUploadPicButton: {
            disabled: !isAdmin || isPictureProcessing,
            className: rightMarginToLeftButtonCls,
            color: 'secondary',
            title: isProfilePicture ? 'Upload new photo' : 'Upload photo',
            component: 'label',
        },
        generalDeletePicButton: { disabled: !isAdmin || isPictureProcessing, className: cx({ [hiddenCls]: !isProfilePicture }), onClick: () => setShowModal(!showModal) },
        companyName: { disabled: !isAdmin, onChange: handleChangeFields, name: 'companyName', value: companyName, placeholder: 'Company Name', error: companyNameError },
        instructionText: { variant: 'body2' },
        // generalLanguage: { disabled: !isAdmin, onChange: handleChangeLanguage, options: LanguagesList, value: language, placeholder: 'Language', error: languageError },
        generalDateFormat: {
            disabled: !isAdmin,
            onChange: handleChangeDateFormat,
            value: dateFormat,
            error: dateFormatError,
        },
        // generalTimeFormat: { disabled: !isAdmin, onChange: handleChangeTimeFormat, value: timeFormat, options: TimeList, placeholder: 'Select a format', error: timeFormatError },

        generalCountry: {
            // disabled: !isAdmin,
            // onChange: handleChangeCountry,
            // value: country,
            // options: countryLov,
            // id: 'country',
            // placeholder: PLACEHOLDER_FIELD_COUNTRY,

            ...countryProps,
            disabled: !isAdmin,
            error: countryError,
        },
        generalStateLabel: {
            // value: stateLabelValue
            ...stateLabelProps,
        },
        generalState: {
            // disabled: !country || !isAdmin,
            // onChange: handleChangeState,
            // value: state,
            // id: 'state',
            // options: stateLov,
            // placeholder: statePlaceholder,

            ...stateProps,
            disabled: !country || !isAdmin,
            error: stateError,
        },
        generalCity: {
            // disabled: !state || !isAdmin,
            // onChange: handleChangeCity,
            // value: city,
            // id: 'city',
            // options: cityLov,
            // placeholder: cityPlaceholder,

            ...cityProps,
            disabled: !state || !isAdmin,
            error: cityError,
        },

        memberCountry: {
            // disabled: !isAdmin,
            // onChange: handleChangeCountry,
            // value: country,
            // options: countryLov,
            // id: 'country',
            // placeholder: PLACEHOLDER_FIELD_COUNTRY,

            ...memberCountryProps,
            disabled: !isAdmin,
            error: memberCountryError,
        },
        memberStateLabel: {
            // value: stateLabelValue
            ...memberStateLabelProps,
        },
        memberState: {
            // disabled: !country || !isAdmin,
            // onChange: handleChangeState,
            // value: state,
            // id: 'state',
            // options: stateLov,
            // placeholder: statePlaceholder,

            ...memberStateProps,
            disabled: !memberCountry || !isAdmin,
            error: memberStateError,
        },
        memberCity: {
            // disabled: !state || !isAdmin,
            // onChange: handleChangeCity,
            // value: city,
            // id: 'city',
            // options: cityLov,
            // placeholder: cityPlaceholder,

            ...memberCityProps,
            disabled: !memberState || !isAdmin,
            error: memberCityError,
        },

        generalCancelButton: { disabled: !isDirty, onClick: () => setConfirmModalOpen(true), className: rightMarginToLeftButtonCls },
        generalSaveButton: { disabled: !isDirty, loading: saveLoading, onClick: handleSaveButton, color: 'primary' },
        generalContinueButton: { onClick: handleClickContinueButton, color: 'primary', loading: saveLoading, disabled: saveLoading },

        heading: { variant: 'h6', value: heading },
        label: { variant: 'body2' },
        picUploadInput: { type: 'file', hidden: true, onChange: handleChangePicUploadInput, accept: '.jpg, .png' },
        confirmModal: {
            title: CONFIRM_DIALOG_TITLE_DELETE_LOGO,
            onClose: () => setShowModal(!showModal),
            onConfirm: handleDeletePic,
            open: showModal,
            message: CONFIRM_DIALOG_MESSAGE_DELETE_LOGO,
        },
        cropModal: {
            open: showCropModal,
            onClose: () => setShowCropModal(false),
            label: 'Profile Picture',
            actionButtonDefns: {
                cancel: { disabled: false, onClick: () => setShowCropModal(false) },
                save: {
                    disabled: false,
                    loading: saveLoading,
                    onClick: handleUploadPic,
                    label: 'Save Changes',
                },
            },
        },
        companyNameErrorText: { hidden: !companyNameError },
        countryErrorText: { hidden: !countryError },
        stateErrorText: { hidden: !stateError },
        cityErrorText: { hidden: !cityError },
        memberCountryErrorText: { hidden: !memberCountryError },
        memberStateErrorText: { hidden: !memberStateError },
        memberCityErrorText: { hidden: !memberCityError },
        // languageErrorText: { hidden: !languageError },
        timeFormatErrorText: { hidden: !timeFormatError },
        dateFormatErrorText: { hidden: !dateFormatError },

        cropper: { file: tempImage, setBlob: setBlob },
        uploadLinearProgress: { variant: 'determinate', value: progress, isHidden: _isNil(progress) },

        cancelModal: {
            title: CONFIRM_DIALOG_TITLE_CANCEL,
            onClose: () => setConfirmModalOpen(false),
            onConfirm: handleClickConfirmCancelModalYesButton,
            open: confirmModal,
            message: CONFIRM_DIALOG_MESSAGE_CANCEL,
        },
        badWordsDialog: {
            title: 'Inappropriate Text is not Allowed!',
            children: (
                <AfitdTypography>
                    The word(s): <b>{badWordsList} </b>
                    does not meet our community guidelines. Inappropriate content cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: openBadWordsDialog,
            onClose: () => clearOutBadWords(),
        },
        badImageDialog: {
            title: 'Inappropriate Images are not Allowed!',
            children: (
                <AfitdTypography>
                    We detected <b>{badImageDialogMessage}</b> in your picture which does not meet our community guidelines. Inappropriate Images cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: openBadImageDialog,
            onClose: async () => {
                setOpenBadImageDialog();
                setShowCropModal(false);
                await resetEmpSeResults({ employerId, setSnackbarMessage, setSnackbarErrorMessage });
            },
        },
        useHeadquarterLocationLink: { onClick: handleClickUseHeadquarterLocationLink },
    };

    // inject classes
    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const initErrors = {
    companyNameError: false,
    stateError: false,
    countryError: false,
    cityError: false,
    // languageError: false,
    dateFormatError: false,
    timeFormatError: false,
    memberCountryError: false,
    memberStateError: false,
    memberCityError: false,
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const General = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    const appContext = useContext(AppContext);
    const {
        // currentUser,
        // userId,
        currentEmployer,
        employerId,
        pendingAccountSetup,
        lovs,
    } = appContext;
    // const stripeCusId = currentEmployer?.stripeCusId;
    // const stripeTestClockId = currentEmployer?.stripeTestClockId;

    const layoutContext = useContext(LayoutContext);
    const { accountSetupGoToSubscription, setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [blob, setBlob] = useState(null);
    const [tempImage, setTempImage] = useState('');
    const [progress, setProgress] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showCropModal, setShowCropModal] = useState(false);
    const [confirmModal, setConfirmModalOpen] = useState(false);
    const [errors, setErrors] = useState(initErrors);
    const [isDirty, setIsDirty] = useState(false);

    const [openBadWordsDialog, setOpenBadWordsDialog] = useState(false);
    const [badWords, setBadWords] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);
    const [openBadImageDialog, setOpenBadImageDialog] = useState(false);
    const [badImageDialogMessage, setBadImageDialogMessage] = useState([]);
    const [isPictureProcessing, setIsPictureProcessing] = useState(false);

    const [data, setData] = useState({ companyName: '', /* language: '', */ dateFormat: '', timeFormat: '', profilePicUrl: '' });

    const {
        // countryLov,
        // stateLov,
        // cityLov,
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        cityLocation,
        setCityLocation,
        newCity,
        // countryCurrency,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
    } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov'),
        setIsDirty,
    });

    const {
        // countryLov,
        // stateLov,
        // setStateLov,
        // cityLov,
        // setCityLov,
        country: memberCountry,
        setCountry: setMemberCountry,
        state: memberState,
        setState: setMemberState,
        city: memberCity,
        setCity: setMemberCity,
        cityLocation: memberCityLocation,
        setCityLocation: setMemberCityLocation,
        newCity: newMemberCity,
        // countryCurrency,
        countryProps: memberCountryProps,
        stateLabelProps: memberStateLabelProps,
        stateProps: memberStateProps,
        cityProps: memberCityProps,
    } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov', []),
        setIsDirty,
    });

    const { companyName = '', profilePicUrl = '', dateFormat = '' /* language = ''*/ } = data;

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const resetFields = useCallback(() => {
        setData({
            ...currentEmployer,
        });

        setCountry(_get(currentEmployer, 'country', null));
        setState(_get(currentEmployer, 'state', null));
        setCity(_get(currentEmployer, 'city', null));
        setCityLocation(_get(currentEmployer, 'cityLocation', null));

        setMemberCountry(_get(currentEmployer, 'members[0].country', null));
        setMemberState(_get(currentEmployer, 'members[0].state', null));
        setMemberCity(_get(currentEmployer, 'members[0].city', null));
        setMemberCityLocation(_get(currentEmployer, 'members[0].cityLocation', null));
    }, [currentEmployer, setCountry, setState, setCity, setCityLocation, setMemberCountry, setMemberState, setMemberCity, setMemberCityLocation]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        resetFields();
    }, [resetFields]);

    // Detect SightEngine Response
    useEffect(() => {
        let seResult = _get(currentEmployer, 'seResult');
        const uploadedPictureStatus = _get(currentEmployer, 'uploadedPictureStatus');

        if (seResult) extractBadContentFromResult({ seResult, setBadImageDialogMessage, setOpenBadImageDialog });

        if (uploadedPictureStatus === UPLOADED_VIDEO_STATUS_PROCESSING) setIsPictureProcessing(true);
        else setIsPictureProcessing(false);
    }, [currentEmployer]);

    // useEffect(() => {
    // detectInappropriatePicture();
    // }, []);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isProfilePicture = profilePicUrl || false;

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChangeFields = evt => {
        const { name, value } = evt.target;
        setIsDirty(true);
        setData({ ...data, [name]: value });
    };

    // const handleChangeLanguage = (evt, option) => setData({ ...data, language: option });

    const handleChangeDateFormat = (evt, option) => {
        const dateFormatCode = option?.code;
        console.log({ dateFormatCode });
        setData({ ...data, dateFormat: dateFormatCode });
        setIsDirty(true);
    };
    // const handleChangeTimeFormat = (evt, option) => setData({ ...data, timeFormat: option });

    // const handleChangeCountry = (evt, option) => {
    //     setCountry(option?.name);
    //     setState(null);
    //     setCity(null);
    //     setCityLocation(null);
    // };
    // const handleChangeState = (evt, option) => {
    //     setState(option?.name);
    //     setCity(null);
    //     setCityLocation(null);
    // };
    // const handleChangeCity = (evt, option) => {
    //     setCity(option);
    // };

    const handleClickConfirmCancelModalYesButton = () => {
        resetFields();
        setConfirmModalOpen(false);
        setIsDirty(false);
        setErrors(initErrors);
    };

    const validate = () => {
        let newErrors = { ...errors };
        _set(newErrors, 'companyNameError', !!!companyName);
        // _set(newErrors, 'languageError', !!!language);
        _set(newErrors, 'dateFormatError', !!!dateFormat);
        // _set(newErrors, 'timeFormatError', !!!timeFormat);
        _set(newErrors, 'countryError', !!!country);
        _set(newErrors, 'stateError', !!!state);
        _set(newErrors, 'cityError', !!!city);

        // only validate these fields during emp account setup
        if (pendingAccountSetup) {
            _set(newErrors, 'memberCountryError', !!!memberCountry);
            _set(newErrors, 'memberStateError', !!!memberState);
            _set(newErrors, 'memberCityError', !!!memberCity);
        }

        setErrors(newErrors);
        return !_includes(newErrors, true);
    };

    const handleSaveButton = async () => {
        if (validate()) {
            setSaveLoading(true);

            const dataToFilter = {
                companyName,
            };
            const response = await moderateUserTextInput({ text: JSON.stringify(dataToFilter) });

            if (!response) {
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED);
                setSaveLoading(false);
            } else if (response?.profanity?.length > 0 || response?.personal?.length > 0 || response?.link?.length > 0) {
                setOpenBadWordsDialog(true);
                setBadWords(response);
                setSaveLoading(false);
            } else {
                await updateEmployerData({
                    data: {
                        ...data,
                        country,
                        state,
                        city,
                        cityLocation,
                        newCity,
                        employerId,
                    },
                    employerId,
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                });
                setSaveLoading(false);
                setIsDirty(false);
            }
        }
    };

    const clearOutBadWords = () => {
        // eslint-disable-next-line no-unused-expressions
        badWords?.profanity?.forEach(badWord => {
            badWord.toLowerCase();

            const regEx = new RegExp(badWord, 'ig');

            if (companyName?.toLowerCase()?.includes(badWord)) {
                const filteredCompanyName = companyName?.replace(regEx, '');
                setData({ ...data, companyName: filteredCompanyName });
            }
        });
        setOpenBadWordsDialog(false);
        // setBadWords({});
    };

    const handleClickContinueButton = async () => {
        if (validate()) {
            setSaveLoading(true);
            const success = await saveEmployerOwnerData({
                companyName,
                dateFormat,
                // language,
                country,
                state,
                city,
                cityLocation,
                newCity,
                memberCountry,
                memberState,
                memberCity,
                memberCityLocation,
                newMemberCity,
                employerId,
                setSnackbarMessage,
                setSnackbarErrorMessage,
            });

            // assuming only owner can see the Continue button.  Create a Stripe customer account.
            // if (!stripeCusId) {
            //     const { firstName, middleName, lastName } = getUserInfoByEmployerId({ currentUser, employerId });
            //     await createStripeCustomer({ userId, employerId, firstName, middleName, lastName, email: currentUser?.email, stripeTestClockId });
            // }

            if (success) {
                setSaveLoading(false);
                accountSetupGoToSubscription();
            }
        }
    };

    const handleChangePicUploadInput = evt => {
        const file = evt.target.files[0];
        if (file?.size < 5000000) {
            setTempImage(file);
            // detectInappropriatePicture(file);
            setShowCropModal(true);
        } else {
            console.log('file is huge');
        }
    };

    const handleUploadPic = async () => {
        setSaveLoading(true);
        await uploadProfilePicture(
            blob,
            employerId,
            // url => setData({ ...data, profilePicUrl: url }),
            currentEmployer,
            setSnackbarMessage,
            setSnackbarErrorMessage,
            setProgress,
            () => {
                setShowCropModal(false);
                setSaveLoading(false);
            },
        );
    };

    const handleDeletePic = () => {
        deleteProfilePicture(employerId, { ...data, profilePicUrl: '' }, setSnackbarMessage, setSnackbarErrorMessage, profilePicUrl);
    };

    const handleClickUseHeadquarterLocationLink = () => {
        setMemberCountry(country);
        setMemberState(state);
        setMemberCity(city);
        setMemberCityLocation(cityLocation);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        isDirty,
        // setIsDirty,
        pendingAccountSetup,
        data,
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        cityLocation,
        setCityLocation,
        countryProps,
        stateLabelProps,
        stateProps,
        cityProps,
        classes,
        handleChangeFields,
        // handleChangeCountry,
        // handleChangeState,
        // handleChangeCity,
        handleSaveButton,
        handleClickContinueButton,
        handleClickConfirmCancelModalYesButton,
        handleUploadPic,
        handleDeletePic,
        // countryLov,
        // stateLov,
        // cityLov,
        currentEmployer,
        showModal,
        setShowModal,
        showCropModal,
        setShowCropModal,
        handleChangePicUploadInput,
        setBlob,
        tempImage,
        errors,
        progress,
        props,
        confirmModal,
        setConfirmModalOpen,
        // handleChangeLanguage,
        handleChangeDateFormat,
        openBadWordsDialog,
        setOpenBadWordsDialog,
        // handleChangeTimeFormat,
        badWords,
        setBadWords,
        clearOutBadWords,
        saveLoading,
        openBadImageDialog,
        setOpenBadImageDialog,
        badImageDialogMessage,
        handleClickUseHeadquarterLocationLink,
        memberCountry,
        memberState,
        memberCity,
        memberCityLocation,
        memberCountryProps,
        memberStateLabelProps,
        memberStateProps,
        memberCityProps,
        employerId,
        setSnackbarMessage,
        setSnackbarErrorMessage,
        isPictureProcessing,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AppContext.Provider value={{ ...appContext }}>
            <AfitdRouterPrompt when={isDirty} />
            <div {...defns.generalMainContainer}>
                <AfitdTypography {...defns.heading} />
                <div {...defns.generalAvatarContainer}>
                    {!isProfilePicture && !isPictureProcessing && (
                        <div {...defns.avatarPlaceholderBox}>
                            <AfitdAvatar {...defns.avatarPlaceholder} />
                        </div>
                    )}
                    {isPictureProcessing ? (
                        <div {...defns.avatarPlaceholderBox}>
                            <AfitdAvatarWithRing {...defns.loadingPictureAvatar} />
                        </div>
                    ) : (
                        <>{isProfilePicture && <AfitdAvatarWithRing {...defns.profilePic} />}</>
                    )}
                    <div {...defns.btnBox}>
                        <div>
                            <AfitdButton {...defns.generalUploadPicButton} style={{ width: '165px' }}>
                                {defns.generalUploadPicButton.title}
                                <input {...defns.picUploadInput} />
                            </AfitdButton>
                            <AfitdButton {...defns.generalDeletePicButton} style={{ marginLeft: 20 }}>
                                Delete
                            </AfitdButton>
                        </div>
                        <AfitdTypography {...defns.instructionText}>JPG and PNG files are the accepted file types.</AfitdTypography>
                    </div>
                </div>
                <div>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Company Name *</AfitdTypography>
                        <AfitdTextField {...defns.companyName} />
                        <span {...defns.companyNameErrorText}>Company Name is required</span>
                    </div>

                    {/* <div {...defns.inputBoxContainer}>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Date Format *</AfitdTypography>
                        <AfitdAutocomplete {...defns.generalDateFormat} />
                        <span {...defns.dateFormatErrorText}>Date Format is required</span>
                    </div>
                    <div {...defns.inputBox}>
                        <AfitdTypography {...defns.label}>Time Format *</AfitdTypography>
                        <AfitdAutocomplete {...defns.generalTimeFormat} />
                        <span {...defns.timeFormatErrorText}>Time Format is required</span>
                    </div>
                </div> */}
                    <div {...defns.inputBoxContainer}>
                        <div {...defns.inputBox}>
                            <AfitdTypography {...defns.label}>Headquarters/Head Office Location *</AfitdTypography>
                            <AfitdCountryAutocomplete {...defns.generalCountry} />
                            <span {...defns.countryErrorText}>Headquarters/Head Office Location is required</span>
                        </div>
                        <div {...defns.inputBox}>
                            <AfitdTypography {...defns.label}>{defns.generalStateLabel.value} *</AfitdTypography>
                            <AfitdStateAutocomplete {...defns.generalState} />
                            <span {...defns.stateErrorText}>State/Province is required</span>
                        </div>
                    </div>
                    <div {...defns.inputBoxContainer}>
                        <div {...defns.inputBox}>
                            <AfitdTypography {...defns.label}>City *</AfitdTypography>
                            <AfitdCityAutocomplete {...defns.generalCity} />
                            <span {...defns.cityErrorText}>City Name is required</span>
                        </div>
                        <div {...defns.inputBox}>
                            <AfitdTypography {...defns.label}>Date Format *</AfitdTypography>
                            <AfitdDateFormatAutocomplete {...defns.generalDateFormat} />
                            <span {...defns.dateFormatErrorText}>Date Format is required</span>
                        </div>
                    </div>
                    {/* <div {...defns.inputBoxContainer}>
                        <div {...defns.inputBox}>
                            <AfitdTypography {...defns.label}>Language *</AfitdTypography>
                            <AfitdAutocompleteLite {...defns.generalLanguage} />
                            <span {...defns.languageErrorText}>Language is required</span>
                        </div>
                      
                    </div> */}
                    {pendingAccountSetup && (
                        <div {...defns.useHeadquarterLocationLinkContainer}>
                            <span {...defns.useHeadquarterLocationLinkSpan}>
                                <AfitdLink {...defns.useHeadquarterLocationLink}>{USE_HEADQUARTER_LOCATION}?</AfitdLink>
                            </span>
                            <Tooltip classes={{ tooltip: classes.toolTipTitle }} placement="right-end" title={TOOLTIP_EMP_MEMBER_USE_COMPANY_LOCATION}>
                                <InfoRoundedIcon {...defns.infoIcon} />
                            </Tooltip>
                        </div>
                    )}
                    {pendingAccountSetup && (
                        <div {...defns.inputBoxContainer}>
                            <div {...defns.inputBox}>
                                <AfitdTypography {...defns.label}>
                                    Your Location *{' '}
                                    <Tooltip classes={{ tooltip: classes.toolTipTitle }} placement="right-end" title={TOOLTIP_EMP_MEMBER_COUNTRY}>
                                        <InfoRoundedIcon {...defns.infoIcon} />
                                    </Tooltip>
                                </AfitdTypography>
                                <AfitdCountryAutocomplete {...defns.memberCountry} />
                                <span {...defns.memberCountryErrorText}>Your Location is required</span>
                            </div>
                            <div {...defns.inputBox}>
                                <AfitdTypography {...defns.label}>{defns.memberStateLabel?.value} *</AfitdTypography>
                                <AfitdStateAutocomplete {...defns.memberState} />
                                <span {...defns.memberStateErrorText}>State/Province is required</span>
                            </div>
                        </div>
                    )}
                    {pendingAccountSetup && (
                        <div {...defns.inputBoxContainer}>
                            <div {...defns.inputBox}>
                                <AfitdTypography {...defns.label}>City *</AfitdTypography>
                                <AfitdCityAutocomplete {...defns.memberCity} />
                                <span {...defns.memberCityErrorText}>City Name is required</span>
                            </div>
                            <div {...defns.inputBox}></div>
                        </div>
                    )}
                </div>
                <AfitdConfirmDialog {...defns.confirmModal} />
                <AfitdEditDialog {...defns.cropModal}>
                    <div {...defns.imgBeforeCropContainer}>
                        <AfitdProfilePicCropper {...defns.cropper} />
                    </div>
                    <div {...defns.uploadLinearProgressContainer}>
                        <AfitdLinearProgress {...defns.uploadLinearProgress} />
                    </div>
                </AfitdEditDialog>

                <div className={classes.buttonBox}>
                    {!pendingAccountSetup && <AfitdButton {...defns.generalCancelButton}>Cancel</AfitdButton>}
                    {!pendingAccountSetup && <AfitdButton {...defns.generalSaveButton}>Save Changes</AfitdButton>}
                    {pendingAccountSetup && <AfitdButton {...defns.generalContinueButton}>Continue</AfitdButton>}
                    {/* <AfitdButton {...defns.generalCancelButton}>Cancel</AfitdButton> */}
                    {/* <AfitdButton {...defns.generalSaveButton}>Save Changes</AfitdButton> */}
                </div>
                <AfitdConfirmDialog {...defns.cancelModal} />
                <AfitdMessageDialog {...defns.badWordsDialog} />
                <AfitdMessageDialog {...defns.badImageDialog} />
            </div>
        </AppContext.Provider>
    );
};

export default General;
